import { IntlShape } from 'react-intl';

import type { TFsmState } from '@/modules/onboarding/fsm-types';
import { FsmState } from '@/modules/onboarding/fsm-types';
import type { IContainerProps } from '@/shared/view/StepLayout';

import { messages } from './messages';

const totalSteps = 5;

type PartialContainerProps = Omit<
  IContainerProps,
  'onSwitchScreen' | 'switchScreenEvents' | 'transitionKey'
>;

const mapFsmStateToCtProps = ({
  stateValue,
  intl,
  partnerFirstName,
}: {
  stateValue: TFsmState;
  intl: IntlShape;
  partnerFirstName: string;
}): PartialContainerProps => {
  switch (stateValue) {
    case FsmState.driverUpload:
    case FsmState.driver:
      return {
        step: 1,
        totalSteps,
        title: intl.formatMessage(messages.helloTitle, { name: partnerFirstName }),
        subTitle: intl.formatMessage(messages.personalInfoSubTitle),
      };
    case FsmState.doYouHaveCar:
      return {
        step: 4,
        totalSteps,
        title: intl.formatMessage(messages.vehicleQualifyTitle),
        textNoButton: intl.formatMessage(messages.skipButtonText),
      };
    case FsmState.partnerPayments:
      return {
        step: 3,
        totalSteps,
        title: intl.formatMessage(messages.paymentDetailsTitle),
      };
    case FsmState.uploadNewCarDocs:
    case FsmState.newCar:
      return {
        step: 5,
        totalSteps,
        title: intl.formatMessage(messages.vehicleInfoTitle),
      };
    case FsmState.payTo:
      return {
        step: 2,
        totalSteps,
        title: intl.formatMessage(messages.payoutsTitle),
      };
    case FsmState.toDo:
      return {
        hideHeader: true,
        hideProgress: true,
      };
    case FsmState.welcome:
      return {
        hideHeader: true,
        hideProgress: true,
      };
    default:
      return {};
  }
};

const mapFsmStateToCtPropsUK = ({
  stateValue,
  intl,
}: {
  stateValue: TFsmState;
  intl: IntlShape;
}): PartialContainerProps => {
  switch (stateValue) {
    case FsmState.licensingAuthority:
      return {
        step: 1,
        totalSteps,
        title: intl.formatMessage(messages.ukLicensingAuthorityStepTitle),
      };
    case FsmState.vehicle:
      return {
        step: 2,
        totalSteps,
        title: intl.formatMessage(messages.vehicleStepTitle),
      };
    case FsmState.driverUpload:
    case FsmState.driver:
      return {
        step: 3,
        totalSteps,
        title: intl.formatMessage(messages.personalInformationStepTitle),
      };
    case FsmState.partnerPayments:
      return {
        step: 5,
        totalSteps,
        title: intl.formatMessage(messages.paymentDetailsTitle),
      };
    case FsmState.payTo:
      return {
        step: 4,
        totalSteps,
        title: intl.formatMessage(messages.payoutsTitle),
      };
    case FsmState.toDo:
      return {
        hideHeader: true,
        hideProgress: true,
      };
    case FsmState.welcome:
      return {
        hideHeader: true,
        hideProgress: true,
      };
    case FsmState.licensingAuthorityMissing:
      return {
        hideHeader: false,
        hideProgress: true,
        title: intl.formatMessage(messages.ukLicensingAuthorityMissingTitle),
      };
    default:
      return {};
  }
};

const mapFsmStateToCtPropsFR = ({
  stateValue,
  intl,
}: {
  stateValue: TFsmState;
  intl: IntlShape;
}): PartialContainerProps => {
  const totalStepsFr = 4;

  switch (stateValue) {
    case FsmState.vehicle:
      return {
        step: 1,
        totalSteps: totalStepsFr,
        title: intl.formatMessage(messages.vehicleStepTitle),
      };
    case FsmState.driverUpload:
    case FsmState.driver:
      return {
        step: 2,
        totalSteps: totalStepsFr,
        title: intl.formatMessage(messages.personalInformationStepTitle),
      };
    case FsmState.payTo:
      return {
        step: 3,
        totalSteps: totalStepsFr,
        title: intl.formatMessage(messages.payoutsTitle),
      };
    case FsmState.partnerPayments:
      return {
        step: 4,
        totalSteps: totalStepsFr,
        title: intl.formatMessage(messages.paymentDetailsTitle),
      };
    case FsmState.toDo:
      return {
        hideHeader: true,
        hideProgress: true,
      };
    case FsmState.welcome:
      return {
        hideHeader: true,
        hideProgress: true,
      };
    default:
      return {};
  }
};

const mapFsmStateToCtPropsUS = ({
  stateValue,
  intl,
}: {
  stateValue: TFsmState;
  intl: IntlShape;
}): PartialContainerProps => {
  switch (stateValue) {
    case FsmState.vehicle:
      return {
        step: 2,
        totalSteps,
        title: intl.formatMessage(messages.vehicleStepTitle),
      };
    case FsmState.driverUpload:
    case FsmState.driver:
      return {
        step: 3,
        totalSteps,
        title: intl.formatMessage(messages.personalInformationStepTitle),
      };
    case FsmState.payTo:
      return {
        step: 4,
        totalSteps,
        title: intl.formatMessage(messages.payoutsTitle),
      };
    case FsmState.partnerPayments:
      return {
        step: 5,
        totalSteps,
        title: intl.formatMessage(messages.paymentDetailsTitle),
      };
    case FsmState.toDo:
      return {
        hideHeader: true,
        hideProgress: true,
      };
    case FsmState.welcome:
      return {
        hideHeader: true,
        hideProgress: true,
      };
    default:
      return {};
  }
};

export {
  mapFsmStateToCtProps,
  mapFsmStateToCtPropsUK,
  mapFsmStateToCtPropsFR,
  mapFsmStateToCtPropsUS,
};
